<script setup lang="ts">
import Button from "primevue/button";
import { ref } from "vue";
import ParticipantConfig from "./participant-configuration/ParticipantConfig.vue";
import { createParticipantAndLogActivity } from "../../utils/ProjectUtils";
import { Participant, Project } from "../../types/PersologOnlineTypes.ts";
import { useI18n } from "vue-i18n";

const { t } = useI18n();

const visible = ref<boolean>(false);
const isLoadingSaveButton = ref<boolean>(false);
const valid = ref<boolean>(true);
const addedParticipants = ref<Participant[]>([]);

const props = defineProps({
    project: {
        type: Object as () => Project,
        required: true,
    },
    disabled: {
        type: Boolean,
        default: false,
    },
    withLabel: {
        type: Boolean,
        default: false,
    },
    withClasses: {
        type: String,
        default: "",
    },
    participants: {
        type: Array as () => Participant[],
        default: () => [],
    }
});

const participantConfigRef = ref<InstanceType<typeof ParticipantConfig> | null>(null);
const emit = defineEmits(["update:participants"]);

function handleValidation(isValid: boolean) {
    valid.value = isValid;
}

function handleDuplicatedParticipants(isDuplicated: boolean) {
    valid.value = !isDuplicated;
}

function handleParticipantUpdate(createdParticipants: Participant[]) {
    addedParticipants.value = createdParticipants;
}

async function saveParticipants() {
    participantConfigRef.value?.addParticipant();
    if (props.project && props.project.id && addedParticipants.value.length > 0) {
        isLoadingSaveButton.value = true;
        createParticipantAndLogActivity(
            props.project.id,
            addedParticipants.value
                .filter((participant) => participant.id === undefined)
                .map((p) => ({
                    ...p,
                })),
        )
            .then(() => {
                emit("update:participants");
                addedParticipants.value = [];
                visible.value = false;
            })
            .catch((error) => {
                console.error(error);
            })
            .finally(() => {
                isLoadingSaveButton.value = false;
            });
    } else {
        emit("update:participants");
        addedParticipants.value = [];
        visible.value = false;
        isLoadingSaveButton.value = false;
    }
}

function clearAddedParticipants() {
    addedParticipants.value = [];
}
</script>

<template>
    <Button
        :id="`add-participants_btn_add-${project.id}`"
        :class="withClasses"
        :disabled="disabled"
        :label="withLabel ? t('project-details.participants.callForAction.add') : ''"
        icon="pi pi-user-plus"
        @click="visible = true"
        v-tooltip.top="{ value: t('project-details.participants.tooltips.add'), showDelay: 500 }"
    />
    <!-- Same width rule as the project dialog it opens from (ProjectDetailsView, RDT-456) -->
    <Dialog
        v-model:visible="visible"
        modal
        :id="`add-participants_dialog_add-${project.id}`"
        :header="t('project-details.participants.heading')"
        :style="{ width: 'clamp(80vw, 1550px, 97vw)', maxWidth: '1600px' }"
        :breakpoints="{ '575px': '97vw' }"
        :dismissable-mask="false"
        :block-scroll="true"
        :draggable="false"
        @hide="clearAddedParticipants"
    >
        <ParticipantConfig
            ref="participantConfigRef"
            class="mt-6"
            :id="project.id?.toString() || '0'"
            :project="project"
            :participants-to-add="addedParticipants"
            :existing-participants="participants"
            @update:valid="handleValidation"
            @update:participants="handleParticipantUpdate"
            @update:duplicated="handleDuplicatedParticipants"
        />
        <div class="flex flex-wrap gap-4 mt-2">
            <div class="flex-1 flex justify-end">
                <Button
                    :id="`add-participants_btn_save-${project.id}`"
                    :label="t('buttons.save')"
                    :loading="isLoadingSaveButton"
                    icon="pi pi-check"
                    @click="saveParticipants"
                    :disabled="!valid || isLoadingSaveButton"
                />
            </div>
        </div>
    </Dialog>
</template>
